import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { DEFAULT_DATE_TIME_RANGE } from '@modules/common/components/DateRangePicker/constants';
import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  NAV_PATHS,
  NOTIFICATION_TIMEOUT_LONG_6S,
} from '@modules/common/constants';
import {
  useGetDefaultTenantTimeRange,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import { useInitSearchStoreFromSearchParams } from '@modules/feed/hooks/useInitSearchStoreFromSearchParams';
import { useIsChatWithFeedbackEnabled } from '@modules/feed/hooks/useIsChatWithFeedbackEnabled';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import {
  getSearchMetadataUrl,
  getSearchQueryFromMetadata,
} from '@modules/feed/utils';
import { Scope, Status, useGetAnalysisByTokenQuery } from 'generated/graphql';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AndOfErrors } from 'v2/core/errors/errors';
import { AndStatuses, IsLoading } from 'v2/core/repository/edge-server/types';
import { parseEQL } from 'v2/core/repository/eql/parser';
import useGetDashboardDeps from 'v2/core/services/dashboard/useGetDashboardDeps';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import useParseSavedSlugInfo from 'v2/core/services/savedSlug/useParseSavedSlugInfo';
import { useSetAnalysisStoreFromFeed } from './useSetAnalysisStoreFromFeed';

export const useFetchSearchAnalysisAndUpdateStore = () => {
  const { showNotification, showErrorNotification } = useNotificationContext();
  const dispatch = useFeedStore(({ dispatch }) => dispatch);
  const { token } = useParseSavedSlugInfo();
  const { data: deps, status: dStatus, error: dError } = useGetDashboardDeps();
  const whereClause = useFeedStore(({ query }) => query.whereClause);
  const viewToken = useAnalysisStore(({ viewToken }) => viewToken);
  const defaultTimeRange = useGetDefaultTenantTimeRange();
  const URLParams = new URLSearchParams(document.location.search);
  const hasFromAndToClause = URLParams.has('from') && URLParams.has('to');
  const navigate = useNavigate();

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();
  const isChatWithFeedbackEnabled = useIsChatWithFeedbackEnabled();

  const {
    user: {
      id: userID,
      tenant: { slug: tenantSlug, timezone },
    },
    tenantID,
    sourceDetailsMap,
  } = deps ?? {};

  const {
    data: analysis,
    loading,
    error: gError,
  } = useGetAnalysisByTokenQuery({
    variables: {
      token: token ?? '',
      tenantID,
      userID,
      includeScheduleOutbounds: true,
    },
    fetchPolicy: 'network-only',
    skip: !token || !sourceDetailsMap,
  });

  const status = AndStatuses([dStatus]);
  const error = AndOfErrors([dError, gError]);
  const isLoading = IsLoading(status) || loading;

  useEffect(() => {
    if (token) {
      dispatch({
        type: SearchActionType.RESET,
      });

      return () => {
        dispatch({
          type: SearchActionType.RESET,
        });
      };
    }
  }, [dispatch, token]);

  useInitSearchStoreFromSearchParams();

  const getSearchMetadata = useCallback(() => {
    if (
      analysis?.getAnalysisByToken?.scope === Scope.SystemGenerated &&
      sourceDetailsMap
    ) {
      const eql = analysis?.getAnalysisByToken.context.eqlExpression;
      const eqlProps = parseEQL(eql, sourceDetailsMap, timezone);

      const whereClause = eqlProps.whereClause ?? EmptyWhereClause;

      const duration =
        (eqlProps.dateRange as DateTimeRange) ?? DEFAULT_DATE_TIME_RANGE;

      return getSearchMetadataUrl({
        tenantSlug,
        duration,
        whereClause,
        isSearchToFeedEnabled,
      });
    }

    return analysis?.getAnalysisByToken?.metadata || window.location.href;
  }, [
    analysis?.getAnalysisByToken?.context,
    analysis?.getAnalysisByToken?.metadata,
    analysis?.getAnalysisByToken?.scope,
    isSearchToFeedEnabled,
    sourceDetailsMap,
    tenantSlug,
    timezone,
  ]);

  useSetAnalysisStoreFromFeed({
    analysis:
      loading || !analysis?.getAnalysisByToken
        ? undefined
        : { ...analysis?.getAnalysisByToken, metadata: getSearchMetadata() },
  });

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: isSearchToFeedEnabled
          ? `Failed to fetch Feed `
          : `Failed to fetch Search`,
        props: { isClosable: true },
      });
    }
  }, [error, showErrorNotification, isSearchToFeedEnabled]);

  useEffect(() => {
    if (analysis?.getAnalysisByToken?.status === Status.Archived) {
      dispatch({
        type: SearchActionType.RESET,
      });
      navigate(`/${isSearchToFeedEnabled ? NAV_PATHS.FEED : NAV_PATHS.SEARCH}`);
      showNotification({
        contentComponent: `This ${
          isSearchToFeedEnabled ? NAV_PATHS.FEED : NAV_PATHS.SEARCH
        } is archived.`,
        props: {
          timeout: NOTIFICATION_TIMEOUT_LONG_6S,
          severity: 'info',
        },
      });
      return;
    }

    if (
      analysis?.getAnalysisByToken &&
      analysis?.getAnalysisByToken?.viewToken &&
      (!viewToken || viewToken !== analysis?.getAnalysisByToken?.viewToken)
    ) {
      dispatch({
        type: SearchActionType.SET_UPDATE_QUERY,
        payload: {
          ...getSearchQueryFromMetadata({
            metadata: getSearchMetadata(),
            getDurationField: !hasFromAndToClause,
            defaultTimeRange,
          }),
        },
      });

      if (
        isChatWithFeedbackEnabled &&
        analysis.getAnalysisByToken.context.prompt
      ) {
        dispatch({
          type: SearchActionType.SET_PROMPT,
          payload: analysis.getAnalysisByToken.context.prompt,
        });
      }
    }
  }, [
    analysis?.getAnalysisByToken,
    defaultTimeRange,
    dispatch,
    getSearchMetadata,
    hasFromAndToClause,
    isSearchToFeedEnabled,
    navigate,
    showNotification,
    viewToken,
    isChatWithFeedbackEnabled,
  ]);

  const hasWhereClause = Boolean(whereClause);

  return {
    isLoading: Boolean(token) && isLoading,
    hasWhereClause: Boolean(hasWhereClause),
    error,
  };
};
